import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
} from '@mui/material';
import apiService from '../services/api';

const AddProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'planned',
    progressPercentage: '0',
    budget: '',
    location: '',
    ward: 'Nyali',
    department: '',
    startDate: '',
    endDate: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [featuredImage, setFeaturedImage] = useState('');
  const [isUploadingFeatured, setIsUploadingFeatured] = useState(false);

  const [updateProjectCode, setUpdateProjectCode] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [updateFormData, setUpdateFormData] = useState({
    title: '',
    description: '',
    status: 'planned',
    progressPercentage: '0',
    budget: '',
    location: '',
    ward: 'Nyali',
    department: '',
    startDate: '',
    endDate: '',
  });
  const [updateError, setUpdateError] = useState('');
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [updateLoaded, setUpdateLoaded] = useState(false);
  const [updateFeaturedImage, setUpdateFeaturedImage] = useState('');
  const [isUploadingUpdateFeatured, setIsUploadingUpdateFeatured] = useState(false);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateChange = (e: any) => {
    const { name, value } = e.target;
    setUpdateFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFeaturedImageChange = async (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setIsUploadingFeatured(true);
    try {
      const res = await apiService.uploadFile(file);
      setFeaturedImage(res.data.fileUrl);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setIsUploadingFeatured(false);
    }
  };

  const handleUpdateFeaturedImageChange = async (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUpdateError('');
    setIsUploadingUpdateFeatured(true);
    try {
      const res = await apiService.uploadFile(file);
      setUpdateFeaturedImage(res.data.fileUrl);
    } catch (err: any) {
      setUpdateError(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setIsUploadingUpdateFeatured(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.title || !formData.description) {
      setError('Title and description are required');
      return;
    }

    setIsLoading(true);

    try {
      await apiService.createProject({
        title: formData.title,
        description: formData.description,
        status: formData.status as 'planned' | 'ongoing' | 'completed' | 'delayed',
        progressPercentage: formData.progressPercentage ? parseInt(formData.progressPercentage, 10) : 0,
        budget: formData.budget ? parseFloat(formData.budget) : undefined,
        location: formData.location ? { latitude: 0, longitude: 0, address: formData.location } : undefined,
        ward: formData.ward,
        department: formData.department,
        startDate: formData.startDate,
        endDate: formData.endDate,
        featuredImage: featuredImage || undefined,
      });

      setSuccessMessage('Project created successfully!');
      setOpenSnackbar(true);

      setTimeout(() => {
        navigate('/projects');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadProject = async () => {
    setUpdateError('');

    if (!updateProjectCode.trim()) {
      setUpdateError('Project serial number or project name is required');
      return;
    }

    setIsUpdateLoading(true);
    setUpdateLoaded(false);

    try {
      const response = await apiService.getProjectByCode(updateProjectCode.trim());
      const project = response.data;

      setUpdateFormData({
        title: project.title || '',
        description: project.description || '',
        status: project.status || 'planned',
        progressPercentage:
          typeof project.progressPercentage === 'number'
            ? project.progressPercentage.toString()
            : '0',
        budget:
          typeof project.budget === 'number' ? project.budget.toString() : '',
        location: project.location?.address || '',
        ward: project.ward || 'Nyali',
        department: project.department || '',
        startDate: project.startDate ? project.startDate.substring(0, 10) : '',
        endDate: project.endDate ? project.endDate.substring(0, 10) : '',
      });

      setUpdateFeaturedImage(project.featuredImage || '');

      setSelectedProjectId(project.id);

      setUpdateLoaded(true);
    } catch (err: any) {
      setUpdateError(
        err.response?.data?.message || 'Failed to load project. Check the code and try again.'
      );
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdateError('');

    if (!selectedProjectId) {
      setUpdateError('Please load a project by code first');
      return;
    }

    if (!updateFormData.title || !updateFormData.description) {
      setUpdateError('Title and description are required');
      return;
    }

    setIsUpdateLoading(true);

    try {
      await apiService.updateProject(selectedProjectId, {
        title: updateFormData.title,
        description: updateFormData.description,
        status: updateFormData.status as 'planned' | 'ongoing' | 'completed' | 'delayed',
        progressPercentage: updateFormData.progressPercentage
          ? parseInt(updateFormData.progressPercentage, 10)
          : 0,
        budget: updateFormData.budget
          ? parseFloat(updateFormData.budget)
          : undefined,
        location: updateFormData.location
          ? { latitude: 0, longitude: 0, address: updateFormData.location }
          : undefined,
        ward: updateFormData.ward,
        department: updateFormData.department,
        startDate: updateFormData.startDate,
        endDate: updateFormData.endDate,
        featuredImage: updateFeaturedImage || undefined,
      });

      setSuccessMessage('Project updated successfully!');
      setOpenSnackbar(true);
    } catch (err: any) {
      setUpdateError(
        err.response?.data?.message || 'Failed to update project'
      );
    } finally {
      setIsUpdateLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ padding: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {'Add New Project'}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={'Project Title'}
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{'Status Category'}</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label={'Status Category'}
                    disabled={isLoading}
                  >
                    <MenuItem value="planned">Planned</MenuItem>
                    <MenuItem value="ongoing">Ongoing</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="delayed">Delayed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{'Status (%)'}</InputLabel>
                  <Select
                    name="progressPercentage"
                    value={formData.progressPercentage}
                    onChange={handleChange}
                    label={'Status (%)'}
                    disabled={isLoading}
                  >
                    <MenuItem value="0">0%</MenuItem>
                    <MenuItem value="10">10%</MenuItem>
                    <MenuItem value="20">20%</MenuItem>
                    <MenuItem value="30">30%</MenuItem>
                    <MenuItem value="40">40%</MenuItem>
                    <MenuItem value="50">50%</MenuItem>
                    <MenuItem value="60">60%</MenuItem>
                    <MenuItem value="70">70%</MenuItem>
                    <MenuItem value="80">80%</MenuItem>
                    <MenuItem value="90">90%</MenuItem>
                    <MenuItem value="100">100%</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={'Description'}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  required
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={'Budget'}
                  name="budget"
                  type="number"
                  value={formData.budget}
                  onChange={handleChange}
                  disabled={isLoading}
                  inputProps={{ step: '0.01' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={'Location'}
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={isUploadingFeatured || isLoading}
                  >
                    {isUploadingFeatured ? 'Uploading Image...' : 'Upload Featured Image'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleFeaturedImageChange}
                    />
                  </Button>
                  {featuredImage && (
                    <Box
                      component="img"
                      src={featuredImage}
                      alt="Featured preview"
                      sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
                    />
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={'Department'}
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{'Ward'}</InputLabel>
                  <Select
                    name="ward"
                    value={formData.ward}
                    onChange={handleChange}
                    label={'Ward'}
                    disabled={isLoading}
                  >
                    <MenuItem value="Port Reitz">Port Reitz</MenuItem>
                    <MenuItem value="Kipevu">Kipevu</MenuItem>
                    <MenuItem value="Airport">Airport</MenuItem>
                    <MenuItem value="Changamwe">Changamwe</MenuItem>
                    <MenuItem value="Chaani">Chaani</MenuItem>
                    <MenuItem value="Jomvu Kuu">Jomvu Kuu</MenuItem>
                    <MenuItem value="Miritini">Miritini</MenuItem>
                    <MenuItem value="Mikindani.">Mikindani.</MenuItem>
                    <MenuItem value="Mjambere">Mjambere</MenuItem>
                    <MenuItem value="Junda">Junda</MenuItem>
                    <MenuItem value="Bamburi">Bamburi</MenuItem>
                    <MenuItem value="Mwakirunge">Mwakirunge</MenuItem>
                    <MenuItem value="Mtopanga">Mtopanga</MenuItem>
                    <MenuItem value="Magogoni">Magogoni</MenuItem>
                    <MenuItem value="Shanzu">Shanzu</MenuItem>
                    <MenuItem value="Frere Town">Frere Town</MenuItem>
                    <MenuItem value="Ziwa la Ng'ombe">Ziwa la Ng'ombe</MenuItem>
                    <MenuItem value="Mkomani">Mkomani</MenuItem>
                    <MenuItem value="Kongowea">Kongowea</MenuItem>
                    <MenuItem value="Kadzandani">Kadzandani</MenuItem>
                    <MenuItem value="Mtongwe">Mtongwe</MenuItem>
                    <MenuItem value="Shika Adabu">Shika Adabu</MenuItem>
                    <MenuItem value="Bofu">Bofu</MenuItem>
                    <MenuItem value="Likoni">Likoni</MenuItem>
                    <MenuItem value="Timbwani">Timbwani</MenuItem>
                    <MenuItem value="Tudor">Tudor</MenuItem>
                    <MenuItem value="Mji wa Kale/Makadara">Mji wa Kale/Makadara</MenuItem>
                    <MenuItem value="Tononoka">Tononoka</MenuItem>
                    <MenuItem value="Shimanzi/Ganjoni">Shimanzi/Ganjoni</MenuItem>
                    <MenuItem value="Majengo">Majengo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={'Proposed Start Date'}
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleChange}
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={'Proposed End Date'}
                  name="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={handleChange}
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularProgress size={24} /> : ('submit')}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/projects')}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        <Box sx={{ mt: 4 }}>
          <Paper elevation={3} sx={{ padding: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {'Update Existing Project'}
            </Typography>

            {updateError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {updateError}
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                label={'Project Serial No / Name'}
                value={updateProjectCode}
                onChange={(e) => setUpdateProjectCode(e.target.value)}
                disabled={isUpdateLoading}
              />
              <Button
                variant="contained"
                onClick={handleLoadProject}
                disabled={isUpdateLoading || !updateProjectCode.trim()}
              >
                {isUpdateLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  'Load Project'
                )}
              </Button>
            </Box>

            {updateLoaded && (
              <form onSubmit={handleUpdateSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={'Project Title'}
                      name="title"
                      value={updateFormData.title}
                      onChange={handleUpdateChange}
                      required
                      disabled={isUpdateLoading}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{'Status Category'}</InputLabel>
                      <Select
                        name="status"
                        value={updateFormData.status}
                        onChange={handleUpdateChange}
                        label={'Status Category'}
                        disabled={isUpdateLoading}
                      >
                        <MenuItem value="planned">Planned</MenuItem>
                        <MenuItem value="ongoing">Ongoing</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="delayed">Delayed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{'Status (%)'}</InputLabel>
                      <Select
                        name="progressPercentage"
                        value={updateFormData.progressPercentage}
                        onChange={handleUpdateChange}
                        label={'Status (%)'}
                        disabled={isUpdateLoading}
                      >
                        <MenuItem value="0">0%</MenuItem>
                        <MenuItem value="10">10%</MenuItem>
                        <MenuItem value="20">20%</MenuItem>
                        <MenuItem value="30">30%</MenuItem>
                        <MenuItem value="40">40%</MenuItem>
                        <MenuItem value="50">50%</MenuItem>
                        <MenuItem value="60">60%</MenuItem>
                        <MenuItem value="70">70%</MenuItem>
                        <MenuItem value="80">80%</MenuItem>
                        <MenuItem value="90">90%</MenuItem>
                        <MenuItem value="100">100%</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={'Description'}
                      name="description"
                      value={updateFormData.description}
                      onChange={handleUpdateChange}
                      multiline
                      rows={4}
                      required
                      disabled={isUpdateLoading}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={'Budget'}
                      name="budget"
                      type="number"
                      value={updateFormData.budget}
                      onChange={handleUpdateChange}
                      disabled={isUpdateLoading}
                      inputProps={{ step: '0.01' }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={'Location'}
                      name="location"
                      value={updateFormData.location}
                      onChange={handleUpdateChange}
                      disabled={isUpdateLoading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        disabled={isUploadingUpdateFeatured || isUpdateLoading}
                      >
                        {isUploadingUpdateFeatured ? 'Uploading Image...' : 'Upload Featured Image'}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleUpdateFeaturedImageChange}
                        />
                      </Button>
                      {updateFeaturedImage && (
                        <Box
                          component="img"
                          src={updateFeaturedImage}
                          alt="Featured preview"
                          sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
                        />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={'Department'}
                      name="department"
                      value={updateFormData.department}
                      onChange={handleUpdateChange}
                      disabled={isUpdateLoading}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{'Ward'}</InputLabel>
                      <Select
                        name="ward"
                        value={updateFormData.ward}
                        onChange={handleUpdateChange}
                        label={'Ward'}
                        disabled={isUpdateLoading}
                      >
                        <MenuItem value="Port Reitz">Port Reitz</MenuItem>
                        <MenuItem value="Kipevu">Kipevu</MenuItem>
                        <MenuItem value="Airport">Airport</MenuItem>
                        <MenuItem value="Changamwe">Changamwe</MenuItem>
                        <MenuItem value="Chaani">Chaani</MenuItem>
                        <MenuItem value="Jomvu Kuu">Jomvu Kuu</MenuItem>
                        <MenuItem value="Miritini">Miritini</MenuItem>
                        <MenuItem value="Mikindani.">Mikindani.</MenuItem>
                        <MenuItem value="Mjambere">Mjambere</MenuItem>
                        <MenuItem value="Junda">Junda</MenuItem>
                        <MenuItem value="Bamburi">Bamburi</MenuItem>
                        <MenuItem value="Mwakirunge">Mwakirunge</MenuItem>
                        <MenuItem value="Mtopanga">Mtopanga</MenuItem>
                        <MenuItem value="Magogoni">Magogoni</MenuItem>
                        <MenuItem value="Shanzu">Shanzu</MenuItem>
                        <MenuItem value="Frere Town">Frere Town</MenuItem>
                        <MenuItem value="Ziwa la Ng'ombe">Ziwa la Ng'ombe</MenuItem>
                        <MenuItem value="Mkomani">Mkomani</MenuItem>
                        <MenuItem value="Kongowea">Kongowea</MenuItem>
                        <MenuItem value="Kadzandani">Kadzandani</MenuItem>
                        <MenuItem value="Mtongwe">Mtongwe</MenuItem>
                        <MenuItem value="Shika Adabu">Shika Adabu</MenuItem>
                        <MenuItem value="Bofu">Bofu</MenuItem>
                        <MenuItem value="Likoni">Likoni</MenuItem>
                        <MenuItem value="Timbwani">Timbwani</MenuItem>
                        <MenuItem value="Tudor">Tudor</MenuItem>
                        <MenuItem value="Mji wa Kale/Makadara">Mji wa Kale/Makadara</MenuItem>
                        <MenuItem value="Shimanzi/Ganjoni">Shimanzi/Ganjoni</MenuItem>
                        <MenuItem value="Majengo">Majengo</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={'Proposed Start Date'}
                      name="startDate"
                      type="date"
                      value={updateFormData.startDate}
                      onChange={handleUpdateChange}
                      disabled={isUpdateLoading}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={'Proposed End Date'}
                      name="endDate"
                      type="date"
                      value={updateFormData.endDate}
                      onChange={handleUpdateChange}
                      disabled={isUpdateLoading}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={isUpdateLoading}
                      >
                        {isUpdateLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          'Update Project'
                        )}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={() => setOpenSnackbar(false)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </Container>
  );
};

export default AddProjectPage;
